import { Platform } from '@angular/cdk/platform';
import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  MatBottomSheet,
  MatBottomSheetRef,
} from '@angular/material/bottom-sheet';
import { Router } from '@angular/router';
import { environment } from '@fantaz/shared/app-config';
import {
  CurrentUser,
  GAEventKeys,
  ShareOptions,
} from '@fantaz/shared/data-access/models';
import { ToastService } from '@fantaz/shared/utils/toast';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { map, Observable } from 'rxjs';
import { ShareDialogComponent } from './share-dialog.component';
import { ShareOption } from './share.model';

export declare type ShareAction =
  | 'onFacebookShare'
  | 'onTwitterShare'
  | 'onCopyLinkShare'
  | 'onNativeShare'
  | 'onWhatsAppShare'
  | 'onMyEmailShare'
  | 'onFantaZEmailShare';
@Injectable({
  providedIn: 'root',
})
export class ShareService {
  #platform = inject(Platform);
  bottomSheet = inject(MatBottomSheet);
  gtmService = inject(GoogleTagManagerService);
  location = inject(Location);
  router = inject(Router);
  http = inject(HttpClient);
  toastService = inject(ToastService);

  shareOptions: ShareOption[] = [
    {
      enabled: true,
      name: 'Share',
      function: 'onNativeShare',
      icon: '/shared/img/share/share.png',
      type: 'native',
      hideOnMobile: false,
    },
    {
      enabled: true,
      name: 'Facebook',
      function: 'onFacebookShare',
      icon: '/shared/img/share/facebook.png',
      type: 'social',
      hideOnMobile: true,
    },
    {
      enabled: true,
      name: 'Twitter',
      function: 'onTwitterShare',
      icon: '/shared/img/social_icon_twitter_black_rounded.png',
      type: 'social',
      hideOnMobile: true,
    },
    {
      enabled: true,
      name: 'WhatsApp',
      function: 'onWhatsAppShare',
      icon: '/shared/img/share/whatsapp.png',
      type: 'social',
      hideOnMobile: true,
    },
    {
      enabled: true,
      name: 'Email',
      function: 'onMyEmailShare',
      icon: '/shared/img/share/email.png',
      type: 'email',
      hideOnMobile: true,
    },
    {
      enabled: true,
      name: 'FantaZ Email',
      function: 'onFantaZEmailShare',
      icon: '/shared/img/share/fantaz.png',
      type: 'email',
      hideOnMobile: false,
    },
  ];
  window: any = window; // TODO: Fix ts error when use Window object
  options: ShareOptions;
  readyFacebookSDK = false;

  get isReadyFacebookSDK() {
    return this.readyFacebookSDK;
  }

  share(options: ShareOptions, params?: { callback?: (params?: any) => void }) {
    // prepare options
    let opts = this.shareOptions;
    this.options = options;
    // check for native share
    const ns = this.canWebShare(options);

    if (options.currentUser) {
      const url = new URL(options.url);

      if (!url.searchParams.has('inviteBy')) {
        url.searchParams.set('inviteBy', options.currentUser.user?.username ?? '');
      }

      options.url = url.toString();
    }

    if (
      options.type !== 'promote' &&
      (this.#platform.ANDROID || this.#platform.IOS) &&
      ns
    ) {
      this.onNativeShare(options);
    } else {
      if (!ns) {
        // remove native share in case of not enabled
        opts = opts.filter((o) => o.function !== 'onNativeShare');
      }

      // remove social links
      if (options.disableSocialLinks) {
        opts = opts.filter((o) => o.type !== 'social');
      }

      let backdropClass = '';

      if (options.type === 'promote') {
        backdropClass = 'promote';
      }

      const ref: MatBottomSheetRef = this.bottomSheet.open(
        ShareDialogComponent,
        {
          data: {
            shareOptions: opts,
            options,
            params,
          },
          disableClose: true,
          panelClass: backdropClass,
          backdropClass,
        },
      );
      ref
        .afterDismissed()
        .subscribe((result: { action: ShareAction; url: string }) => {
          this.shareResult(result);
          if (params && params.callback) {
            params.callback();
          }
          if (typeof result === 'string' && options.type === 'promote') {
            const pathWithoutHash = this.location.path(false);
            this.router.navigateByUrl(pathWithoutHash);
            this.router.onSameUrlNavigation = 'reload';
          }
        });
    }
  }

  shareResult(result: { action: ShareAction; url: string }) {
    if (result && typeof this[result.action] === 'function') {
      this.options.url = result.url;
      this[result.action](this.options);
    }
  }
  async onNativeShare(options: ShareOptions) {
    const canShare = this.canWebShare(options);
    if (canShare) {
      try {
        const url = this.addCampaignParameters(
          options,
          'Native',
          'Native_sharing',
        );
        await this.window.navigator.share({
          title: options.title,
          text: options.text,
          url: url,
        });
        this.sendGAEvent('Native Share', options.url, options.currentUser);
      } catch (error) {
        console.log(error);
      }
    }
  }
  async onFacebookShare(options: ShareOptions) {
    const url = this.addCampaignParameters(
      options,
      'Facebook',
      'Facebook_sharing',
    );
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}&amp;src=sdkpreparse`;

    this.sendGAEvent('Facebook', options.url, options.currentUser);

    this.openUrl(facebookUrl);
  }
  onTwitterShare(options: ShareOptions) {
    const twt = {
      shareText: options.text || 'Join me! ',
      via: 'FantaZCorp',
      hashtags: 'FantaZCorp, fantaz',
    };
    const url = this.addCampaignParameters(
      options,
      'Twitter',
      'Twitter_sharing',
    );
    const twitterUrl =
      'https://twitter.com/intent/tweet?text=' +
      twt.shareText +
      '&url=' +
      url +
      '&hashtags=' +
      twt.hashtags +
      '&via=' +
      twt.via;
    this.sendGAEvent('Twitter', options.url, options.currentUser);
    this.openUrl(twitterUrl);
  }
  onWhatsAppShare(options: ShareOptions) {
    this.sendGAEvent('WhatsApp', options.url, options.currentUser);
    const url = this.addCampaignParameters(
      options,
      'WhatsApp',
      'WhatsApp_sharing',
    );
    const text = options.text ? options.text + ' - ' : '';
    const message = `${text}` + encodeURIComponent(url);
    const whatsApp_url = 'https://api.whatsapp.com/send?text=' + message;
    this.openUrl(whatsApp_url);
  }
  private addCampaignParameters(
    options: ShareOptions,
    medium: string,
    campaign: string,
  ) {
    const url = new URL(options.url);
    url.searchParams.append('utm_source', 'fantaz_share_tools');
    url.searchParams.append('utm_medium', medium);
    url.searchParams.append('utm_campaign', campaign);
    return url.href;
  }

  onFantaZEmailShare(options: ShareOptions) {
    const isAuth = options.currentUser != null;
    // create email links
    const invitationLink = new URL(options.url);
    const fantazLink = new URL(environment.baseURL);
    const secondaryJoinLink = new URL(
      options.url +
        (isAuth === true
          ? '?sponsorFromMail=' + options.currentUser?.user.username
          : ''),
    );
    // attach email campaign data
    this.attachEmailCampaign(invitationLink, {
      utm_source: 'fantaz_share_tools',
      utm_medium: 'email',
      utm_campaign: 'mailto_sharing',
    });
    this.attachEmailCampaign(fantazLink, {
      utm_source: 'fantaz_share_tools',
      utm_medium: 'email',
      utm_campaign: 'mailto_sharing',
    });
    this.attachEmailCampaign(secondaryJoinLink, {
      utm_source: 'fantaz_share_tools',
      utm_medium: 'email',
      utm_campaign: 'mailto_sharing',
    });
    const invitation = invitationLink.toString();
    const fantaz = fantazLink.toString();
    const secondary = secondaryJoinLink.toString();
    // prepare email
    let subject = 'FantaZ Invitation';
    let body = `FantaZ Invitation
    Hello,
    ${
      isAuth
        ? `You have been invited by ${options.currentUser?.user.firstName} ${options.currentUser?.user.lastName}`
        : 'You have been invited'
    } to join ${fantaz} . FantaZ is the ultimate gaming platform where you have fun while making money. You'll find lots of fun games to play 24 hours a day, and live tournaments surrounding special events. We're constantly adding new games so be sure to check the games and tournament areas for the latest.
    Join me at FantaZ by clicking ${invitation} or click here ${secondary} to explore the site.`;
    //  replace spaces and line breaks
    subject = encodeURIComponent(subject).replace(/%0A/g, '%0D%0A');
    body = encodeURIComponent(body).replace(/%0A/g, '%0D%0A');
    this.sendGAEvent('MyEmail', options.url, options.currentUser);
    this.openUrl(`mailto:?subject=${subject}&body=${body}`);
  }
  onCopyLinkShare(options: ShareOptions) {
    const url = this.addCampaignParameters(
      options,
      'CopyLink',
      'CopyLink_sharing',
    );
    this.window.navigator.clipboard.writeText(url).then(() => {
      const message = 'The url link was copied!';
      this.toastService.openSnackBar(message || 'Undefined client error', {
        duration: 5000,
        horizontalPosition: 'center',
      });
      this.sendGAEvent('Link', options.url, options.currentUser);
    });
  }
  onMyEmailShare(options: ShareOptions) {
    // user share navigator api to shared content
    const url = this.addCampaignParameters(options, 'Email', 'Email_sharing');
    const data = {
      title: options.title || 'Join Fantaz',
      text: options.text || `Check out Fantaz`,
      url: url,
    };
    this.sendGAEvent('WebShare', options.url, options.currentUser);
    if (this.window.navigator.share) {
      this.window.navigator
        .share(data)
        .then(() => {
          console.log('Successful share');
        })
        .catch((error: any) => {
          console.log('Error sharing', error);
        });
    }
  }

  attachEmailCampaign(
    link: URL,
    params: {
      utm_source: string;
      utm_medium: string;
      utm_campaign: string;
    },
  ) {
    link.searchParams.append('utm_source', params.utm_source);
    link.searchParams.append('utm_medium', params.utm_medium);
    link.searchParams.append('utm_campaign', params.utm_campaign);
  }
  openUrl(url: string) {
    try {
      this.window.open(url, '_blank').focus();
    } catch (error) {
      console.log('Error with share link: ' + url, error);
    }
  }
  shareByEmail(profileLink: string, currentUser: CurrentUser) {
    const secondaryJoinLink = new URL(
      environment.baseURL + '?sponsorFromMail=' + currentUser.user.username,
    );

    const invitationLink = new URL(profileLink);
    const fantazLink = new URL(environment.baseURL);
    // attach email campaign data
    this.attachEmailCampaign(invitationLink, {
      utm_source: 'fantaz_share_tools',
      utm_medium: 'email',
      utm_campaign: 'mailto_sharing',
    });

    const invitation = invitationLink.toString();
    const fantaz = fantazLink.toString();
    const secondary = secondaryJoinLink.toString();
    // prepare email
    let subject = 'FantaZ Invitation';
    let body = `FantaZ Invitation
Hello,
You have been invited by ${currentUser.user?.firstName} ${currentUser.user?.lastName} to join ${fantaz}. FantaZ is the ultimate gaming platform where you have fun while making money. You'll find lots of fun games to play 24 hours a day, and live tournaments surrounding special events. We're constantly adding new games so be sure to check the games and tournament areas for the latest.
Join me at FantaZ by clicking ${invitation} or click here ${secondary} to explore the site.`;
    //  replace spaces and line breaks
    subject = encodeURIComponent(subject).replace(/%0A/g, '%0D%0A');
    body = encodeURIComponent(body).replace(/%0A/g, '%0D%0A');
    this.sendGAEvent('MyEmail', profileLink, currentUser);
    this.openUrl(`mailto:?subject=${subject}&body=${body}`);
  }
  sendGAEvent(
    type: string,
    profileLink: string,
    currentUser: CurrentUser | null | undefined,
  ) {
    const url = new URL(profileLink);
    const genderVideo = url.searchParams.get('mg') || '';
    let utm_campaign = '';
    if (genderVideo) {
      utm_campaign = 'genderVideo=' + genderVideo;
    }
    const data = {
      event: GAEventKeys.USER_SHARE_PROFILE_LINK,
      social_name: type,
      profile_link: profileLink,
      timestamp: Date.now(),
      utm_source: type,
      utm_medium: 'join-link',
      utm_campaign: utm_campaign,
      user_name: 'anonymous',
      user_id: '',
    };
    if (currentUser && currentUser.user) {
      data.user_name = currentUser.user.username
        ? currentUser.user.username
        : 'anonymous';
      data.user_id = currentUser.user.uuid;
    }
    try {
      this.gtmService.pushTag(data);
    } catch (error) {
      console.log(error);
    }
  }
  public canWebShare(options: ShareOptions): boolean {
    if (
      typeof this.window.navigator.share == 'function' &&
      typeof this.window.navigator.canShare == 'function'
    ) {
      try {
        const canShare = this.window.navigator.canShare({
          title: options.title,
          text: options.text,
          url: options.url,
        });
        return canShare;
      } catch (e) {
        console.log(`Your system doesn't sharing: ${options.url}.`, e);
      }
    } else {
      console.log(`Your system doesn't support sharing.`);
    }
    return false;
  }

  inviteMember(
    email: string,
    profileLink: string,
    currentUser: CurrentUser,
    options?: { callback?(): () => void },
  ): Observable<any> {
    this.sendGAEvent('FantaZ Invitation', profileLink, currentUser);
    const url = profileLink.replace(environment.baseURL, '');
    const endpoint = `${environment.apiBaseUrl}/core/client/api/member/invitation`;

    return this.http.post<any>(endpoint, { email, url }).pipe(
      map((data: any) => {
        if (options && options.callback) {
          options.callback();
        }
        return data;
      }),
    );
  }

  getInvitationLink(args: {
    isPromote: boolean;
    username?: string;
    baseUrl: string;
  }): URL {
    const { isPromote, username, baseUrl } = args;
    let profileLink: URL;
    let url;
    if (isPromote) {
      if (username) {
        url = new URL(baseUrl + '/' + username);
        this.setInviteBy(url, username);
      } else {
        url = new URL(baseUrl);
      }
      profileLink = url;
    } else {
      url = new URL(baseUrl);
      this.setInviteBy(url, username);
      profileLink = url;
    }
    return profileLink;
  }
  private setInviteBy(url: URL, username?: string) {
    if (username) {
      const params = url.searchParams;
      params.append('inviteBy', username);
    }
  }
}
